import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import PrecisionManufacturingRoundedIcon from '@mui/icons-material/PrecisionManufacturingRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AppNavbar from '../components/AppNavbar';
import CapabilityExecutionProfiles from '../components/CapabilityExecutionProfiles';
import CapabilityLocationFields from '../components/CapabilityLocationFields';
import Header from '../components/Header';
import InventoryImage from '../components/InventoryImage';
import SideMenu from '../components/SideMenu';
import TableThumbnail from '../components/TableThumbnail';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';
import { canManageBusinessUnit } from '../utils/businessUnitAccess';
import { capabilityInputsForProfiles } from '../utils/capabilityExecutionProfiles';
import {
  ACCEPTANCE_MODES,
  CAPABILITY_STATUSES,
  CAPABILITY_VISIBILITIES,
  FULFILLMENT_MODES,
  additionalInputsForPayload,
  additionalRequestSchemaFromInputs,
  fieldsFromAdditionalRequestSchema,
  formatCapabilityLabel,
  matchingProfileFromCriteria,
  matchingStateFromProfile,
  outputsForPayload,
} from '../utils/capabilityContract';
import { isAmxAdmin } from '../utils/auth';
import {
  AdditionalInputsEditor,
  AssetPicker,
  ContractFieldList,
  MatchingProfileEditor,
  OutputsEditor,
  RequestFormPreviewDialog,
} from './CapabilityCreatePage';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const tabs = [
  { value: 'overview', label: 'Overview' },
  { value: 'request', label: 'Request & Matching' },
  { value: 'contract', label: 'Inputs & Outputs' },
  { value: 'assets', label: 'Assets' },
  { value: 'fulfillment', label: 'Fulfillment' },
  { value: 'settings', label: 'Settings' },
];

function formatTimestamp(value) {
  if (!value) {
    return 'N/A';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function optionLabel(options, value) {
  return options.find((option) => option.value === value)?.label || formatCapabilityLabel(value);
}

function editableOutputs(outputs) {
  return (Array.isArray(outputs) ? outputs : []).map((output, index) => ({
    id: output.output_id || `${output.key}-${index}`,
    output_id: output.output_id || '',
    key: output.key || '',
    name: output.name || '',
    description: output.description || '',
    kind: output.kind || 'file',
    semantic_type: output.semantic_type || '',
    required: Boolean(output.required),
    data_schema: output.data_schema || {},
    display_order: output.display_order ?? index,
  }));
}

function locationSettingsForPayload(settings) {
  const latitudeText = String(settings.latitude ?? '').trim();
  const longitudeText = String(settings.longitude ?? '').trim();
  if (Boolean(latitudeText) !== Boolean(longitudeText)) {
    throw new Error('Latitude and Longitude must be provided together.');
  }

  const latitude = latitudeText ? Number(latitudeText) : null;
  const longitude = longitudeText ? Number(longitudeText) : null;
  if (latitudeText && (!Number.isFinite(latitude) || latitude < -90 || latitude > 90)) {
    throw new Error('Latitude must be a number between -90 and 90.');
  }
  if (longitudeText && (!Number.isFinite(longitude) || longitude < -180 || longitude > 180)) {
    throw new Error('Longitude must be a number between -180 and 180.');
  }

  return {
    ...settings,
    location_address: String(settings.location_address || '').trim() || null,
    latitude,
    longitude,
  };
}

function normalizeSelectOptions(options) {
  return (Array.isArray(options) ? options : [])
    .map((option) => {
      if (option && typeof option === 'object') {
        const value = String(option.value ?? option.key ?? option.label ?? '').trim();
        return value ? { value, label: String(option.label ?? value) } : null;
      }
      const value = String(option ?? '').trim();
      return value ? { value, label: value } : null;
    })
    .filter(Boolean);
}

function formatCriterionConfig(criterion, field) {
  const config = criterion.config || {};
  if (field?.match_mode === 'numeric_range') {
    const parts = [];
    if (config.min !== null && config.min !== undefined && config.min !== '') {
      parts.push(`Min ${config.min}`);
    }
    if (config.max !== null && config.max !== undefined && config.max !== '') {
      parts.push(`Max ${config.max}`);
    }
    return parts.join(', ') || 'Range not set';
  }
  if (field?.match_mode === 'allowed_values') {
    const options = normalizeSelectOptions(field.options);
    return (config.allowed_values || [])
      .map((value) => options.find((option) => option.value === value)?.label || value)
      .join(', ') || 'No values selected';
  }
  if (field?.match_mode === 'boolean_support') {
    return formatCapabilityLabel(config.support || 'either');
  }
  return String(config.value ?? 'N/A');
}

function MatchingProfileSummary({ criteria, sharedFields, preservedProfile }) {
  const fieldsByKey = new Map((sharedFields || []).map((field) => [field.key, field]));
  const legacyEntries = Object.entries(preservedProfile || {}).filter(([key, value]) => (
    key !== 'schema' && value !== null && typeof value !== 'undefined' && value !== ''
  ));
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableBody>
          {criteria.length ? criteria.map((criterion) => {
            const field = fieldsByKey.get(criterion.field_key);
            return (
              <TableRow key={criterion.id || criterion.field_key}>
                <TableCell sx={{ width: 240, fontWeight: 700 }}>
                  {field?.display_name || 'Matching Criterion'}
                </TableCell>
                <TableCell>{field ? formatCriterionConfig(criterion, field) : 'Unavailable field'}</TableCell>
              </TableRow>
            );
          }) : null}
          {legacyEntries.map(([_key, value], index) => (
            <TableRow key={`legacy-${index}`}>
              <TableCell sx={{ width: 240, fontWeight: 700 }}>Legacy Criterion {index + 1}</TableCell>
              <TableCell sx={{ wordBreak: 'break-word' }}>
                {typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')}
              </TableCell>
            </TableRow>
          ))}
          {!criteria.length && !legacyEntries.length ? (
            <TableRow>
              <TableCell>
                <Typography variant="body2" color="text.secondary">No deterministic matching criteria set.</Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </Paper>
  );
}

function OverviewPanel({ capability }) {
  const rows = [
    ['Description', capability.description || capability.properties?.description || 'N/A'],
    ['Type', formatCapabilityLabel(capability.capability_type || capability.type)],
    ['Owning Business Unit', capability.business_unit_display_name || capability.business_unit_code || capability.business_unit_id],
    ['Need Category', capability.category_display_name || capability.category_name || capability.category_id],
    ['Status', optionLabel(CAPABILITY_STATUSES, capability.status)],
    ['Visibility', optionLabel(CAPABILITY_VISIBILITIES, capability.visibility)],
    ['Fulfillment Mode', optionLabel(FULFILLMENT_MODES, capability.fulfillment_mode)],
    ['Acceptance Mode', optionLabel(ACCEPTANCE_MODES, capability.acceptance_mode)],
    ['Direct Requests', capability.allow_direct_requests ? 'Allowed' : 'Not allowed'],
    ['Location Address', capability.location_address || 'N/A'],
    [
      'Coordinates',
      capability.latitude !== null && capability.latitude !== ''
        && capability.longitude !== null && capability.longitude !== ''
        && Number.isFinite(Number(capability.latitude)) && Number.isFinite(Number(capability.longitude))
        ? `${Number(capability.latitude).toFixed(6)}, ${Number(capability.longitude).toFixed(6)}`
        : 'N/A',
    ],
    ['Updated', formatTimestamp(capability.updated_at)],
  ];

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Table size="small">
        <TableBody>
          {rows.map(([label, value]) => (
            <TableRow key={label}>
              <TableCell sx={{ width: 240, fontWeight: 700 }}>{label}</TableCell>
              <TableCell sx={{ wordBreak: 'break-word' }}>{value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

function OutputsReadOnly({ outputs }) {
  const normalizedOutputs = Array.isArray(outputs) ? outputs : [];
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.default' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          Expected Outputs
        </Typography>
      </Box>
      {normalizedOutputs.length ? (
        <Stack divider={<Divider />} sx={{ px: 2 }}>
          {normalizedOutputs.map((output) => (
            <Stack
              key={output.output_id || output.key}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              justifyContent="space-between"
              sx={{ py: 1.25 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                  {output.name || formatCapabilityLabel(output.key)}
                </Typography>
                {output.description ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                    {output.description}
                  </Typography>
                ) : null}
              </Box>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={formatCapabilityLabel(output.kind)} variant="outlined" />
                <Chip
                  size="small"
                  color={output.required ? 'primary' : 'default'}
                  variant={output.required ? 'filled' : 'outlined'}
                  label={output.required ? 'Required Output' : 'Optional Output'}
                />
                {Object.keys(output.data_schema || {}).length ? (
                  <Chip size="small" label="Schema configured" variant="outlined" />
                ) : null}
              </Stack>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">No outputs defined.</Typography>
        </Box>
      )}
    </Paper>
  );
}

function AssetsTable({ assets }) {
  const normalizedAssets = Array.isArray(assets) ? assets : [];
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
      <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.default' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Inventory2RoundedIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Associated Supporting Assets
          </Typography>
          <Chip size="small" label={normalizedAssets.length} variant="outlined" />
        </Stack>
      </Box>
      {normalizedAssets.length ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Asset</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {normalizedAssets.map((asset) => (
              <TableRow key={asset.asset_id} hover>
                <TableCell sx={{ width: 74 }}>
                  <TableThumbnail
                    fileId={asset.asset_image_id}
                    alt={asset.asset_name || 'Asset image'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {asset.asset_name || 'Unnamed Asset'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {asset.source_capability_asset_id || asset.asset_id}
                  </Typography>
                </TableCell>
                <TableCell>{asset.asset_type_display_name || formatCapabilityLabel(asset.asset_type)}</TableCell>
                <TableCell>{asset.location_label || 'N/A'}</TableCell>
                <TableCell>{formatTimestamp(asset.last_status_ts || asset.updated_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">No supporting assets associated.</Typography>
        </Box>
      )}
    </Paper>
  );
}

export default function CapabilityDetailPage(props) {
  const { capabilityDefinitionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const partnerProfile = useSelector((state) => state.auth.partnerProfile);
  const [tab, setTab] = useState(location.state?.initialTab || 'overview');
  const [capability, setCapability] = useState(null);
  const [effectiveSchema, setEffectiveSchema] = useState(null);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState([]);
  const [additionalInputs, setAdditionalInputs] = useState([]);
  const [matchingCriteria, setMatchingCriteria] = useState([]);
  const [preservedMatchingProfile, setPreservedMatchingProfile] = useState({});
  const [outputs, setOutputs] = useState([]);
  const [settings, setSettings] = useState({
    status: 'draft',
    visibility: 'partner',
    allow_direct_requests: false,
    acceptance_mode: 'manual',
    fulfillment_mode: 'manual',
    location_address: '',
    latitude: '',
    longitude: '',
  });
  const [loading, setLoading] = useState(true);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [savingSection, setSavingSection] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState(location.state?.feedback || '');

  const canManage = useMemo(() => {
    if (!capability) {
      return false;
    }
    return canManageBusinessUnit(partnerProfile, capability.business_unit_id, capability.partner_id);
  }, [capability, partnerProfile]);

  const sharedFields = effectiveSchema?.shared_fields || [];
  const additionalFields = effectiveSchema?.additional_fields || [];
  const providerPreviewFields = useMemo(() => {
    try {
      return fieldsFromAdditionalRequestSchema(additionalRequestSchemaFromInputs(additionalInputs));
    } catch (_error) {
      return additionalFields;
    }
  }, [additionalFields, additionalInputs]);
  const executionProfileInputs = useMemo(
    () => capabilityInputsForProfiles(sharedFields, providerPreviewFields),
    [sharedFields, providerPreviewFields],
  );

  async function loadCapability() {
    setLoading(true);
    setError('');
    try {
      const capabilityResponse = await axiosInstance.get(`/capabilities/${encodeURIComponent(capabilityDefinitionId)}`);
      const nextCapability = capabilityResponse.data || null;
      let nextEffectiveSchema = null;

      try {
        const schemaResponse = await axiosInstance.get(`/capabilities/${encodeURIComponent(capabilityDefinitionId)}/request-schema`);
        nextEffectiveSchema = schemaResponse.data?.effective_request_schema || null;
      } catch (_schemaError) {
        nextEffectiveSchema = null;
      }

      const matchingState = matchingStateFromProfile(
        nextCapability?.matching_profile,
        nextEffectiveSchema?.shared_fields || [],
      );
      setCapability(nextCapability);
      setEffectiveSchema(nextEffectiveSchema);
      setSelectedAssetIds((nextCapability?.assets || []).map((asset) => asset.asset_id).filter(Boolean));
      setAdditionalInputs(fieldsFromAdditionalRequestSchema(nextCapability?.additional_request_schema));
      setMatchingCriteria(matchingState.criteria);
      setPreservedMatchingProfile(matchingState.preservedProfile);
      setOutputs(editableOutputs(nextCapability?.outputs));
      setSettings({
        status: nextCapability?.status || 'draft',
        visibility: nextCapability?.visibility || 'partner',
        allow_direct_requests: Boolean(nextCapability?.allow_direct_requests),
        acceptance_mode: nextCapability?.acceptance_mode || 'manual',
        fulfillment_mode: nextCapability?.fulfillment_mode || 'manual',
        location_address: nextCapability?.location_address || '',
        latitude: nextCapability?.latitude ?? '',
        longitude: nextCapability?.longitude ?? '',
      });
    } catch (err) {
      setCapability(null);
      setError(err?.response?.data?.message || err?.message || 'Unable to load capability.');
    } finally {
      setLoading(false);
    }
  }

  async function loadAvailableAssets(nextCapability) {
    const sourceCapability = nextCapability || capability;
    if (!sourceCapability?.business_unit_id) {
      return;
    }
    setAssetsLoading(true);
    try {
      const endpoint = isAmxAdmin(user) ? '/assets' : '/partners/user/assets';
      const response = await axiosInstance.get(endpoint, {
        params: { business_unit_id: sourceCapability.business_unit_id },
      });
      setAvailableAssets(Array.isArray(response.data?.assets) ? response.data.assets : []);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to load assets.');
    } finally {
      setAssetsLoading(false);
    }
  }

  useEffect(() => {
    loadCapability();
  }, [capabilityDefinitionId]);

  useEffect(() => {
    if (capability?.business_unit_id) {
      loadAvailableAssets(capability);
    }
  }, [capability?.business_unit_id]);

  const saveRequestSchema = async () => {
    if (!capability) {
      return;
    }
    setSavingSection('request');
    setError('');
    setFeedback('');
    try {
      const sharedKeys = new Set(sharedFields.map((field) => field.key));
      additionalInputs.forEach((input) => {
        const key = String(input.key || '').trim();
        if (key && sharedKeys.has(key)) {
          throw new Error(`Provider input "${key}" conflicts with a shared Need Category field.`);
        }
      });
      await axiosInstance.put(
        `/partners/${encodeURIComponent(capability.partner_id)}/capabilities/${encodeURIComponent(capability.capability_definition_id)}/request-schema`,
        {
          additional_inputs: additionalInputsForPayload(additionalInputs),
          matching_profile: matchingProfileFromCriteria(
            matchingCriteria,
            sharedFields,
            preservedMatchingProfile,
          ),
        },
      );
      setFeedback('Request contract updated.');
      await loadCapability();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to save request contract.');
    } finally {
      setSavingSection('');
    }
  };

  const handlePreviewRequestForm = () => {
    try {
      additionalRequestSchemaFromInputs(additionalInputs);
      setError('');
      setPreviewOpen(true);
    } catch (err) {
      setError(err?.message || 'Unable to preview request form.');
    }
  };

  const saveOutputs = async () => {
    if (!capability) {
      return;
    }
    setSavingSection('outputs');
    setError('');
    setFeedback('');
    try {
      await axiosInstance.put(
        `/partners/${encodeURIComponent(capability.partner_id)}/capabilities/${encodeURIComponent(capability.capability_definition_id)}/outputs`,
        { outputs: outputsForPayload(outputs) },
      );
      setFeedback('Output definitions updated.');
      await loadCapability();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to save outputs.');
    } finally {
      setSavingSection('');
    }
  };

  const saveAssets = async () => {
    if (!capability) {
      return;
    }
    setSavingSection('assets');
    setError('');
    setFeedback('');
    try {
      await axiosInstance.patch(
        `/partners/${encodeURIComponent(capability.partner_id)}/capabilities/${encodeURIComponent(capability.capability_definition_id)}`,
        { asset_ids: selectedAssetIds },
      );
      setFeedback('Supporting assets updated.');
      await loadCapability();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to save supporting assets.');
    } finally {
      setSavingSection('');
    }
  };

  const saveSettings = async (patch = settings) => {
    if (!capability) {
      return;
    }
    setSavingSection('settings');
    setError('');
    setFeedback('');
    try {
      const payload = locationSettingsForPayload(patch);
      await axiosInstance.patch(
        `/partners/${encodeURIComponent(capability.partner_id)}/capabilities/${encodeURIComponent(capability.capability_definition_id)}/settings`,
        payload,
      );
      setFeedback('Capability settings updated.');
      await loadCapability();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to save capability settings.');
    } finally {
      setSavingSection('');
    }
  };

  const deleteCapability = async () => {
    if (!capability || !window.confirm(`Delete capability "${capability.capability_name || 'Capability'}"? This cannot be undone.`)) {
      return;
    }
    setSavingSection('delete');
    setError('');
    setFeedback('');
    try {
      await axiosInstance.delete(
        `/partners/${encodeURIComponent(capability.partner_id)}/capabilities/${encodeURIComponent(capability.capability_definition_id)}`,
      );
      navigate('/my-capabilities', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to delete capability.');
      setSavingSection('');
    }
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />

        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Capabilities', href: '/my-capabilities' },
              { label: capability?.capability_name || 'Capability' },
            ]}
          />

          {loading ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={24} />
              <Typography>Loading capability...</Typography>
            </Stack>
          ) : error && !capability ? (
            <Alert severity="error">{error}</Alert>
          ) : capability ? (
            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <InventoryImage
                    fileId={capability.capability_image_id}
                    alt={capability.capability_name || 'Capability image'}
                    height={72}
                    sx={{ width: 104, flexShrink: 0 }}
                  />
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PrecisionManufacturingRoundedIcon color="primary" />
                      <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        {capability.capability_name || 'Unnamed Capability'}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {capability.partner_display_name || capability.partner_code || 'Partner'} / {capability.business_unit_display_name || capability.business_unit_code || 'Business Unit'}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={optionLabel(CAPABILITY_STATUSES, capability.status)} color={capability.status === 'active' ? 'success' : 'default'} />
                  <Chip label={optionLabel(CAPABILITY_VISIBILITIES, capability.visibility)} variant="outlined" />
                  {canManage ? (
                    <Button
                      color="error"
                      variant="outlined"
                      startIcon={<DeleteRoundedIcon />}
                      onClick={deleteCapability}
                      disabled={Boolean(savingSection)}
                    >
                      {savingSection === 'delete' ? 'Deleting...' : 'Delete'}
                    </Button>
                  ) : null}
                  {capability.status === 'active' && capability.allow_direct_requests ? (
                    <Button
                      component={RouterLink}
                      to={`/capabilities/${encodeURIComponent(capability.capability_definition_id)}/request`}
                      variant="contained"
                      startIcon={<SendRoundedIcon />}
                    >
                      Request Capability
                    </Button>
                  ) : null}
                  <Button component={RouterLink} to="/my-capabilities" startIcon={<ArrowBackRoundedIcon />}>
                    Back
                  </Button>
                </Stack>
              </Stack>

              {feedback ? <Alert severity="success">{feedback}</Alert> : null}
              {error ? <Alert severity="error">{error}</Alert> : null}

              <Tabs value={tab} onChange={(_event, value) => setTab(value)} variant="scrollable" allowScrollButtonsMobile>
                {tabs.map((item) => (
                  <Tab key={item.value} value={item.value} label={item.label} />
                ))}
              </Tabs>

              {tab === 'overview' ? (
                <OverviewPanel capability={capability} />
              ) : null}

              {tab === 'request' ? (
                <Stack spacing={2}>
                  <ContractFieldList
                    title={`Shared Need Information: ${capability.category_display_name || capability.category_name || 'Need Category'}`}
                    fields={sharedFields}
                    emptyLabel="No shared fields are defined for this Need Category."
                    locked
                    collapsedByDefault
                  />
                  {canManage ? (
                    <>
                      <MatchingProfileEditor
                        criteria={matchingCriteria}
                        sharedFields={sharedFields}
                        onChange={setMatchingCriteria}
                      />
                      <Button
                        variant="contained"
                        startIcon={<SaveRoundedIcon />}
                        onClick={saveRequestSchema}
                        disabled={savingSection === 'request'}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        {savingSection === 'request' ? 'Saving...' : 'Save Request Contract'}
                      </Button>
                    </>
                  ) : (
                    <MatchingProfileSummary
                      criteria={matchingCriteria}
                      sharedFields={sharedFields}
                      preservedProfile={preservedMatchingProfile}
                    />
                  )}
                </Stack>
              ) : null}

              {tab === 'contract' ? (
                <Stack spacing={2}>
                  <ContractFieldList
                    title="Shared Need Information"
                    fields={sharedFields}
                    emptyLabel="No inherited inputs."
                    locked
                    collapsedByDefault
                  />
                  {canManage ? (
                    <>
                      <AdditionalInputsEditor
                        inputs={additionalInputs}
                        onChange={setAdditionalInputs}
                        onPreview={handlePreviewRequestForm}
                      />
                      <OutputsEditor outputs={outputs} onChange={setOutputs} />
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          variant="contained"
                          startIcon={<SaveRoundedIcon />}
                          onClick={saveRequestSchema}
                          disabled={savingSection === 'request'}
                        >
                          {savingSection === 'request' ? 'Saving...' : 'Save Inputs'}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<SaveRoundedIcon />}
                          onClick={saveOutputs}
                          disabled={savingSection === 'outputs'}
                        >
                          {savingSection === 'outputs' ? 'Saving...' : 'Save Outputs'}
                        </Button>
                      </Stack>
                    </>
                  ) : (
                    <>
                      <ContractFieldList
                        title="Additional Capability Inputs"
                        fields={additionalFields}
                        emptyLabel="No provider inputs defined."
                      />
                      <OutputsReadOnly outputs={capability.outputs} />
                    </>
                  )}
                </Stack>
              ) : null}

              {tab === 'assets' ? (
                <Stack spacing={2}>
                  <AssetsTable assets={capability.assets} />
                  {canManage ? (
                    <>
                      <CapabilityExecutionProfiles
                        assets={capability.assets || []}
                        capabilityInputs={executionProfileInputs}
                        partnerId={capability.partner_id}
                        capabilityId={capability.capability_definition_id}
                        canManage={canManage}
                        fulfillmentMode={settings.fulfillment_mode}
                      />
                      <AssetPicker
                        assets={availableAssets}
                        selectedAssetIds={selectedAssetIds}
                        loading={assetsLoading}
                        onChange={setSelectedAssetIds}
                      />
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          variant="contained"
                          startIcon={<SaveRoundedIcon />}
                          onClick={saveAssets}
                          disabled={savingSection === 'assets'}
                        >
                          {savingSection === 'assets' ? 'Saving...' : 'Save Asset Associations'}
                        </Button>
                        <Button
                          component={RouterLink}
                          to={`/assets?business_unit_id=${encodeURIComponent(capability.business_unit_id)}`}
                          variant="outlined"
                          startIcon={<Inventory2RoundedIcon />}
                        >
                          Open Assets
                        </Button>
                        <Button
                          component={RouterLink}
                          to={`/business-units/${encodeURIComponent(capability.business_unit_id)}/assets/new?capability_id=${encodeURIComponent(capability.capability_definition_id)}&return_to=${encodeURIComponent(location.pathname)}`}
                          variant="outlined"
                          startIcon={<AddRoundedIcon />}
                        >
                          Create New Asset
                        </Button>
                      </Stack>
                    </>
                  ) : null}
                </Stack>
              ) : null}

              {tab === 'fulfillment' ? (
                <Stack spacing={2}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <SettingsRoundedIcon color="primary" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                          Fulfillment
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label={`Fulfillment: ${optionLabel(FULFILLMENT_MODES, capability.fulfillment_mode)}`} />
                        <Chip label={`Acceptance: ${optionLabel(ACCEPTANCE_MODES, capability.acceptance_mode)}`} />
                        <Chip label={capability.allow_direct_requests ? 'Direct Requests Allowed' : 'Direct Requests Off'} variant="outlined" />
                      </Stack>
                      {Array.isArray(capability.integrations) && capability.integrations.length ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Integration</TableCell>
                              <TableCell>Gateway</TableCell>
                              <TableCell>Configuration</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {capability.integrations.map((integration) => (
                              <TableRow key={integration.integration_id}>
                                <TableCell>{formatCapabilityLabel(integration.integration_type)}</TableCell>
                                <TableCell>{integration.gateway_id || 'N/A'}</TableCell>
                                <TableCell sx={{ wordBreak: 'break-word' }}>{JSON.stringify(integration.configuration || {})}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : null}
                    </Stack>
                  </Paper>
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.default' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Asset Transport Fields
                      </Typography>
                    </Box>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Asset</TableCell>
                          <TableCell>Connector</TableCell>
                          <TableCell>Telemetry Topic</TableCell>
                          <TableCell>Actions Topic</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(capability.assets || []).length ? capability.assets.map((asset) => (
                          <TableRow key={asset.asset_id}>
                            <TableCell>{asset.asset_name || asset.asset_id}</TableCell>
                            <TableCell>{asset.amx_connector_id || 'N/A'}</TableCell>
                            <TableCell sx={{ wordBreak: 'break-word' }}>{asset.telemetry_topic || 'N/A'}</TableCell>
                            <TableCell sx={{ wordBreak: 'break-word' }}>{asset.actions_topic || 'N/A'}</TableCell>
                          </TableRow>
                        )) : (
                          <TableRow>
                            <TableCell colSpan={4}>
                              <Typography variant="body2" color="text.secondary">No associated assets.</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                </Stack>
              ) : null}

              {tab === 'settings' ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SettingsRoundedIcon color="primary" />
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Publishing Settings
                      </Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        select
                        label="Status"
                        value={settings.status}
                        onChange={(event) => setSettings((current) => ({ ...current, status: event.target.value }))}
                        disabled={!canManage}
                        fullWidth
                      >
                        {CAPABILITY_STATUSES.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        label="Visibility"
                        value={settings.visibility}
                        onChange={(event) => setSettings((current) => ({ ...current, visibility: event.target.value }))}
                        disabled={!canManage}
                        fullWidth
                      >
                        {CAPABILITY_VISIBILITIES.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        select
                        label="Fulfillment Mode"
                        value={settings.fulfillment_mode}
                        onChange={(event) => setSettings((current) => ({ ...current, fulfillment_mode: event.target.value }))}
                        disabled={!canManage}
                        fullWidth
                      >
                        {FULFILLMENT_MODES.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        label="Acceptance Mode"
                        value={settings.acceptance_mode}
                        onChange={(event) => setSettings((current) => ({ ...current, acceptance_mode: event.target.value }))}
                        disabled={!canManage}
                        fullWidth
                      >
                        {ACCEPTANCE_MODES.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                    <CapabilityLocationFields
                      locationAddress={settings.location_address}
                      latitude={settings.latitude}
                      longitude={settings.longitude}
                      onChange={(patch) => setSettings((current) => ({ ...current, ...patch }))}
                      disabled={!canManage}
                    />
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        icon={<LockRoundedIcon />}
                        label={settings.allow_direct_requests ? 'Direct Requests Allowed' : 'Direct Requests Off'}
                        color={settings.allow_direct_requests ? 'success' : 'default'}
                        onClick={canManage ? () => setSettings((current) => ({
                          ...current,
                          allow_direct_requests: !current.allow_direct_requests,
                        })) : undefined}
                      />
                    </Stack>
                    {canManage ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          variant="contained"
                          startIcon={<SaveRoundedIcon />}
                          onClick={() => saveSettings()}
                          disabled={savingSection === 'settings'}
                        >
                          {savingSection === 'settings' ? 'Saving...' : 'Save Settings'}
                        </Button>
                        {settings.status !== 'retired' ? (
                          <Button
                            color="warning"
                            variant="outlined"
                            onClick={() => saveSettings({ ...settings, status: 'retired' })}
                            disabled={savingSection === 'settings'}
                          >
                            Retire Capability
                          </Button>
                        ) : null}
                      </Stack>
                    ) : null}
                  </Stack>
                </Paper>
              ) : null}
            </Stack>
          ) : (
            <Alert severity="error">Capability not found.</Alert>
          )}
          <RequestFormPreviewDialog
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            sharedFields={sharedFields}
            providerFields={providerPreviewFields}
          />
        </Box>
      </Box>
    </AppTheme>
  );
}
